/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, observable } from 'mobx';
import { OmitFunctions } from 'src/core/stores/types';

export class ConsentStore {
    @observable public consentedCategories: Record<WcpConsent.ConsentCategories, boolean>;

    @observable public innerConsentObject: Omit<WcpConsent.SiteConsent, 'isConsentRequired'>;

    constructor(data?: Partial<OmitFunctions<ConsentStore>>) {
        this.innerConsentObject = null;
        this.consentedCategories = data?.consentedCategories ?? {
            Advertising: false,
            Analytics: false,
            Required: false,
            SocialMedia: false
        };
    }

    /**
     * Gets whether the consent inner object is initialized or not.
     */
    @computed
    public get isInitialized(): boolean {
        return this.innerConsentObject !== null;
    }

    /**
     * Gets whether consent is required for this region
     * or not.
     */
    @computed
    public get isRequired(): boolean {
        return this.isInitialized ? (<WcpConsent.SiteConsent>this.innerConsentObject).isConsentRequired : undefined;
    }

    /**
     * Whether you can drop analytics cookies or not.
     */
    @computed
    public get isAnalyticsConsented(): boolean {
        return this.isInitialized && (!this.isRequired || this.consentedCategories.Analytics);
    }

    /**
     * Sets the WCP consent object that contains
     * native methods set by the library.
     *
     * @param consentObject The consent object to set.
     */
    @action
    public setInnerConsentObject(consentObject: WcpConsent.SiteConsent): void {
        this.innerConsentObject = consentObject;
    }

    /**
     * Sets the categories of cookies that the user has consented for.
     *
     * @param categories The cookie categories the user consented for.
     */
    @action
    public setConsentedCategories(categories: Record<WcpConsent.ConsentCategories, boolean>): void {
        this.consentedCategories = categories;
    }
}
